import { Card, Grid, Stack, Text } from "@mantine/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildStageBuckets, computeStageDistributions } from "../utils/cptStageMetrics";
import CPTStageBoxPlot from "./CPTStageBoxPlot";
import CPTTrialChipStrip from "./CPTTrialChipStrip";
import CPTSectionHeader from "./CPTSectionHeader";
import type {
  ContinuousPerformanceStageEvent,
  HeadVelocity,
  PupilSizeData,
  Stimulus,
  StimulusResponse,
} from "types/tests";

interface CPTTrialDynamicsSectionProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  stimuli?: Stimulus[] | null;
  responses?: StimulusResponse[] | null;
  headVelocityData?: HeadVelocity[];
  pupilSizeData?: PupilSizeData[];
  sectionNumber?: number;
}

const CPTTrialDynamicsSection: React.FC<CPTTrialDynamicsSectionProps> = ({
  stageEvents,
  stimuli,
  responses,
  headVelocityData,
  pupilSizeData,
  sectionNumber,
}) => {
  const { t } = useTranslation();
  const buckets = useMemo(() => buildStageBuckets(stageEvents), [stageEvents]);
  const distributions = useMemo(
    () => computeStageDistributions(buckets, responses, headVelocityData, pupilSizeData),
    [buckets, responses, headVelocityData, pupilSizeData],
  );

  const rtStages = distributions.map((d) => ({ stage: d.stage, values: d.hitRtMs }));
  const velocityStages = distributions.map((d) => ({
    stage: d.stage,
    values: d.headVelocity.map((v) => v * 100),
  }));
  const pupilStages = distributions.map((d) => ({ stage: d.stage, values: d.pupilSize }));

  const hasAnyDistribution =
    rtStages.some((s) => s.values.length) ||
    velocityStages.some((s) => s.values.length) ||
    pupilStages.some((s) => s.values.length);

  const ns = "extraTests.continuousPerformance.result.distributions";
  return (
    <Stack gap="xl">
      <CPTSectionHeader number={sectionNumber} title={t(`${ns}.title`)} />

      <Card withBorder radius="md" p="lg">
        <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="md">
          {t(`${ns}.cardEyebrow`)}
        </Text>
        {hasAnyDistribution ? (
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 4 }} className="cpt-col-4">
              <CPTStageBoxPlot
                title={t(`${ns}.rt.title`)}
                unit={t(`${ns}.rt.unit`)}
                decimals={0}
                yMin={0}
                stages={rtStages}
                caption={t(`${ns}.rt.caption`)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }} className="cpt-col-4">
              <CPTStageBoxPlot
                title={t(`${ns}.headVelocity.title`)}
                unit={t(`${ns}.headVelocity.unit`)}
                decimals={1}
                yMin={0}
                stages={velocityStages}
                caption={t(`${ns}.headVelocity.caption`)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }} className="cpt-col-4">
              <CPTStageBoxPlot
                title={t(`${ns}.pupil.title`)}
                unit={t(`${ns}.pupil.unit`)}
                decimals={2}
                stages={pupilStages}
                caption={t(`${ns}.pupil.caption`)}
              />
            </Grid.Col>
          </Grid>
        ) : (
          <Text c="dimmed">{t(`${ns}.noData`)}</Text>
        )}
      </Card>

      <CPTTrialChipStrip stageEvents={stageEvents} stimuli={stimuli} responses={responses} />
    </Stack>
  );
};

export default CPTTrialDynamicsSection;
